// Routes.jsx
import React from "react";
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
} from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import EventDetails from "./pages/event-details";
import LoginPage from "./pages/login";
import ForgotPassword from "./pages/login/ForgotPassword";
import Dashboard from "./pages/dashboard";
import RegisterPage from "./pages/register";
import RegistrationWithOTP from "./pages/register/RegistrationWithOTP";
import CreateEvent from "./pages/create-event";
import Help from "./pages/help";
import Settings from "./pages/settings";
import Profile from "./pages/profile";
import AuthenticatedLayout from "./components/Layout/AuthenticatedLayout";
import PublicLayout from "./components/Layout/PublicLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Events from "pages/events";
import Analytics from "pages/analytics";
import AnalyticsDetails from "pages/analytics/AnalyticsDetails";
import AddExpense from "./pages/AddExpense";
import SpendWiseDashboard from "./pages/SpendWiseDashboard";
import ExpenseDetails from "./pages/ExpenseDetails";
import Expenses from "./pages/Expenses";
import Reports from "./pages/Reports";
import Budgets from "./pages/Budgets";
import GoogleCallback from "./pages/auth/GoogleCallback";

// Keep the original dashboard import for client-expense-loop
import ClientExpenseLoopDashboard from "./pages/dashboard/index";

// Component to handle login redirect logic
const LoginRedirect = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginPage />;
};

const Routes = () => {
  return (
    <BrowserRouter basename="/">
      <AuthProvider>
        <ErrorBoundary>
          <ScrollToTop />
          <RouterRoutes>
            {/* Root redirect */}
            <Route path="/" element={<LoginRedirect />} />

            {/* Public routes - no navbar/sidebar */}
            <Route element={<PublicLayout />}>
              <Route path="/login" element={<LoginRedirect />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/google/callback" element={<GoogleCallback />} />
            </Route>

            {/* Protected routes - all pages render within AuthenticatedLayout */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout />
                </ProtectedRoute>
              }
            >
              {/* All these routes will render inside the <Outlet /> in AuthenticatedLayout */}
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="create-event" element={<CreateEvent />} />
              <Route path="events/:id" element={<EventDetails />} />
              <Route path="events" element={<Events />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="analytics/:type" element={<AnalyticsDetails />} />
              <Route path="profile" element={<Profile />} />
              <Route path="help" element={<Help />} />
              <Route path="settings" element={<Settings />} />
              
              {/* Spend-wise routes */}
              <Route path="spend-wise-dashboard" element={<SpendWiseDashboard />} />
              <Route path="add-expense" element={<AddExpense />} />
              {/* <Route path="expense-details" element={<ExpenseDetails />} /> */}
              <Route path="expenses" element={<Expenses />} />
              <Route path="expenses/:id" element={<ExpenseDetails />} />
              <Route path="reports" element={<Reports />} />
              <Route path="budgets" element={<Budgets />} />
            </Route>

            {/* 404 page */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
